import { Box, SxProps, Theme, useTheme } from '@mui/material';

interface FlexContainerProps {
  ref?: React.RefObject<HTMLDivElement>;
  children?: React.ReactNode;
  component?: 'div' | 'form' | 'ol' | 'p' | 'ul';
  direction: 'column' | 'row';
  overflow?: 'auto' | 'hidden' | 'visible';
  overflowY?: 'auto' | 'hidden' | 'visible';
  overflowX?: 'auto' | 'hidden' | 'visible';
  overflowAllVisible?: boolean;
  gap?: number;
  justifyContent?:
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
    | 'stretch';
  justifySelf?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  alignSelf?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  height?: string;
  maxHeight?: string;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  flexShrink?: number;
  flexGrow?: number;
  flexWrap?: boolean;
  margin?: number | string;
  padding?: number | string;
  wordWrap?: 'normal' | 'nowrap' | 'break-word';
  noWrap?: boolean;
  color?: string;
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  hoverColor?: string;
  border?: string;
  borderRadius?: 'lessRounded' | 'slightlyRounded' | 'moreRounded' | 'rounded';
  transparent?: boolean;
  sx?: SxProps;
}

export const FlexContainer = ({
  ref,
  children,
  component = 'div',
  direction,
  overflow = 'auto',
  overflowY = 'auto',
  overflowX = 'auto',
  overflowAllVisible = false,
  gap = 1,
  justifyContent = 'flex-start',
  justifySelf,
  alignItems = 'stretch',
  alignSelf,
  height = '100%',
  maxHeight,
  width = 'auto',
  minWidth,
  maxWidth,
  flexShrink,
  flexGrow,
  flexWrap,
  margin,
  padding,
  wordWrap,
  noWrap = false,
  color,
  backgroundColor,
  hoverBackgroundColor,
  hoverColor,
  border,
  borderRadius,
  transparent,
  sx,
}: FlexContainerProps) => {
  const theme = useTheme();

  const borderRadiusValue = theme.app.borderRadius[borderRadius] || undefined;

  const sxProps = {
    display: 'flex',
    flexDirection: direction,
    gap: gap,
    overflow: overflow,
    overflowY: overflowY,
    overflowX: overflowX,
    ...(overflowAllVisible && {
      overflow: 'visible',
      overflowX: 'visible',
      overflowY: 'visible',
    }),
    justifyContent: justifyContent,
    ...(justifySelf && { justifySelf }),
    alignItems: alignItems,
    ...(alignSelf && { alignSelf }),
    height: height,
    ...(maxHeight && { maxHeight }),
    width: width,
    ...(wordWrap && { wordWrap }),
    ...(noWrap && { whiteSpace: 'nowrap' }),
    ...(minWidth && { minWidth }),
    ...(maxWidth && { maxWidth }),
    ...(margin && { margin }),
    ...(padding && { padding }),
    ...(flexShrink !== undefined && { flexShrink }),
    ...(flexGrow !== undefined && { flexGrow }),
    ...(flexWrap && { flexWrap: 'wrap' }),
    ...(border && { border }),
    ...(borderRadiusValue && { borderRadius: borderRadiusValue }),
    boxSizing: 'border-box',
    ...(color && { color }),
    ...(backgroundColor && { backgroundColor }),
    ...(transparent && {
      backgroundColor: 'transparent',
    }),
    ...(hoverBackgroundColor &&
      hoverColor && {
        '&:hover': {
          cursor: 'pointer',
          backgroundColor: hoverBackgroundColor || 'transparent',
          color: hoverColor || 'inherit',
        },
      }),
    ...sx,
  } as SxProps<Theme>;

  return (
    <Box ref={ref} component={component} sx={sxProps}>
      {children}
    </Box>
  );
};
